import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseMutation } from "@/hooks/useSupabaseMutation";
import { TenantCorporateSettings } from "@/types/tenant";
import type { Json } from "@/integrations/supabase/types";

export const tenantSettingsKeys = {
  all: ["tenant-settings"] as const,
  byTenant: (tenantId: string) => ["tenant-settings", tenantId] as const,
};

export interface TenantSettingsData {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  pdf_logo_url: string | null;
  settings: TenantCorporateSettings;
}

export function useTenantSettingsQuery(tenantId: string | undefined) {
  return useQuery({
    queryKey: tenantSettingsKeys.byTenant(tenantId!),
    queryFn: async (): Promise<TenantSettingsData> => {
      const { data, error } = await supabase
        .from("tenants")
        .select("id, name, slug, logo_url, pdf_logo_url, settings")
        .eq("id", tenantId!)
        .single();

      if (error) throw error;

      return {
        ...data,
        settings: (data.settings || {}) as TenantCorporateSettings,
      } as TenantSettingsData;
    },
    enabled: !!tenantId,
  });
}

export interface TenantSettingsMutationPayload {
  name: string;
  logo_url: string | null;
  pdf_logo_url: string | null;
  settings: TenantCorporateSettings;
}

export function useTenantSettingsMutation(tenantId: string | undefined) {
  return useSupabaseMutation<void, TenantSettingsMutationPayload>({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from("tenants")
        .update({
          name: payload.name,
          logo_url: payload.logo_url,
          pdf_logo_url: payload.pdf_logo_url,
          settings: payload.settings as unknown as Json,
        })
        .eq("id", tenantId!)
        .select("id");

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("Update failed: no rows affected (insufficient permissions)");
      }
    },
    invalidateKeys: tenantId ? tenantSettingsKeys.byTenant(tenantId) : undefined,
    toastType: "sonner",
  });
}
